import { UpdateTask } from '@griply/types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  BellNotifications16,
  ChevronRight,
  Flag,
  TagEmpty16,
  Target,
} from 'shared/assets/icons';
import { TaskDateQuickActions } from 'shared/components/connected/task-date-quick-actions';
import { TaskPriorityQuickActions } from 'shared/components/connected/task-priority-quick-actions';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { Task } from 'shared/types/task';
import { getPlatformShortcut } from 'shared/utils/get-platform-shortcut';

import { MenuGroups, SubMenu } from './types';

export type EditListProps = {
  tasks: Task[];
  onClose: () => void;
  openSubMenu: (subMenu: SubMenu) => void;
  onUpdate?: (task: Task, updates: UpdateTask) => void;
  registerItem?: (
    group: MenuGroups,
    index: number,
    element: HTMLButtonElement | null,
  ) => void;
  registerGroup?: MenuGroups;
};

export const EditList: React.FC<EditListProps> = ({
  tasks,
  onClose,
  openSubMenu,
  onUpdate,
  registerItem,
  registerGroup,
}) => {
  const { t } = useTranslation();
  const updateLinkedItem = () => openSubMenu(SubMenu.LinkedItem);
  const updateDeadlineDeadline = () => openSubMenu(SubMenu.DeadlineDeadline);
  const updateReminderTime = () => openSubMenu(SubMenu.ReminderTime);
  const selectDate = () => openSubMenu(SubMenu.SelectDate);
  const updateTags = () => openSubMenu(SubMenu.Tags);

  return (
    <>
      <PopupMenuList hasBottomBorder>
        <TaskDateQuickActions
          tasks={tasks}
          onSelectDate={selectDate}
          onClose={onClose}
          onUpdate={onUpdate}
        />
        <TaskPriorityQuickActions
          tasks={tasks}
          onClose={onClose}
          onUpdate={onUpdate}
        />
      </PopupMenuList>

      <PopupMenuList hasBottomBorder>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={updateLinkedItem}
            start={<Icon icon={Target} />}
            end={<Icon icon={ChevronRight} />}
            endColor={Colors.Grey600}
            shortcut={['G']}
            ref={(el) => registerGroup && registerItem?.(registerGroup, 0, el)}
          >
            {t('context-menus.task.multi-select.options.linked-item.label')}
          </PopupMenuButton>
        </PopupMenuListItem>

        <PopupMenuListItem>
          <PopupMenuButton
            onClick={updateDeadlineDeadline}
            start={<Icon icon={Flag} />}
            end={<Icon icon={ChevronRight} />}
            endColor={Colors.Grey600}
            shortcut={getPlatformShortcut(['Shift', 'D'])}
            ref={(el) => registerGroup && registerItem?.(registerGroup, 1, el)}
          >
            {t(
              'context-menus.task.multi-select.options.deadline-deadline.label',
            )}
          </PopupMenuButton>
        </PopupMenuListItem>

        <PopupMenuListItem>
          <PopupMenuButton
            onClick={updateTags}
            start={<Icon icon={TagEmpty16} />}
            end={<Icon icon={ChevronRight} />}
            endColor={Colors.Grey600}
            shortcut={['T']}
            ref={(el) => registerGroup && registerItem?.(registerGroup, 2, el)}
          >
            {t('context-menus.task.multi-select.options.tags.label')}
          </PopupMenuButton>
        </PopupMenuListItem>

        <PopupMenuListItem>
          <PopupMenuButton
            onClick={updateReminderTime}
            start={<Icon icon={BellNotifications16} />}
            end={<Icon icon={ChevronRight} />}
            endColor={Colors.Grey600}
            shortcut={['R']}
            ref={(el) => registerGroup && registerItem?.(registerGroup, 3, el)}
          >
            {t('context-menus.task.multi-select.options.reminder-time.label')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>
    </>
  );
};
